import React, { useState, useEffect } from 'react';
import { Modal, ModalAction } from '../../common/Modal';
import { toast } from 'react-toastify';
import { SupplierCard_SupplierProductFragment } from '../../../../codegen/graphql';
import SupplierCard_importVariations from './SupplierCard_importVariations';
import { DEFAULT_TRANSLATION_LANG, getTranslatedValue } from '../../../../common/getTranslatedValue';
import { mergeTranslations, TranslatedValue, Translations, TranslationEdit } from '../../common/TranslatedValue';

interface SkuPropertyMapping {
  skuPropertyId: string;
  skuPropertyName: Translations;
  typeName: Translations;
  ignore: boolean;
  values: {
    propertyValueId: string;
    propertyValueName: Translations;
    propertyValueImage: string | null;
    typeValueName: Translations;
    skip: boolean;
  }[];
}

interface ImportVariationsModalProps {
  adId: string;
  onClose: () => void;
  product: SupplierCard_SupplierProductFragment;
}

function toTranslations(value: Translations | string | null | undefined): Translations {
  if (value == null) return {};
  if (typeof value === 'string') return value ? { [DEFAULT_TRANSLATION_LANG]: value } : {};
  return { ...value };
}

export function ImportVariationsModal({ adId, onClose, product }: ImportVariationsModalProps) {
  const [propertyMappings, setPropertyMappings] = useState<SkuPropertyMapping[]>([]);

  // Extract unique sku properties from all variations
  useEffect(() => {
    if (!product || !product.variations) return;

    // Collect all unique sku properties and their values
    const skuPropertiesMap = new Map<string, SkuPropertyMapping>();

    product.variations.forEach(variation => {
      if (!variation.typeValues || variation.typeValues.length === 0) return;
      
      variation.typeValues.forEach((property) => {
        const propertyId = property.type.id;
        
        if (!skuPropertiesMap.has(propertyId)) {
          const typeName = toTranslations(property.type.type);
          skuPropertiesMap.set(propertyId, {
            skuPropertyId: propertyId,
            skuPropertyName: typeName,
            typeName: { ...typeName },
            ignore: false,
            values: []
          });
        }
        
        const propertyMapping = skuPropertiesMap.get(propertyId)!;
        
        // Add property value if it doesn't exist yet
        const propertyValueId = property.id;
        const valueExists = propertyMapping.values.some(v => v.propertyValueId === propertyValueId);
        
        if (!valueExists) {
          const typeValueName = toTranslations(property.value);
          propertyMapping.values.push({
            propertyValueId: propertyValueId,
            propertyValueName: typeValueName,
            typeValueName: { ...typeValueName },
            skip: false,
            propertyValueImage: property.extraData?.image || null
          });
        }
      });
    });
    
    setPropertyMappings(Array.from(skuPropertiesMap.values()));
  }, [product]);

  const handlePropertyTypeNameChange = (propertyId: string, updates: TranslationEdit[]) => {
    setPropertyMappings(prev => 
      prev.map(prop => 
        prop.skuPropertyId === propertyId 
          ? { ...prop, typeName: mergeTranslations(prop.typeName, updates) } 
          : prop
      )
    );
  };

  const handlePropertyIgnoreToggle = (propertyId: string) => {
    setPropertyMappings(prev => 
      prev.map(prop => 
        prop.skuPropertyId === propertyId 
          ? { ...prop, ignore: !prop.ignore } 
          : prop
      )
    );
  };

  const handleValueTypeNameChange = (propertyId: string, valueId: string, updates: TranslationEdit[]) => {
    setPropertyMappings(prev => 
      prev.map(prop => {
        if (prop.skuPropertyId !== propertyId) return prop;
        
        return {
          ...prop,
          values: prop.values.map(val => 
            val.propertyValueId === valueId 
              ? { ...val, typeValueName: mergeTranslations(val.typeValueName, updates) } 
              : val
          )
        };
      })
    );
  };

  const handleValueSkipToggle = (propertyId: string, valueId: string) => {
    setPropertyMappings(prev => 
      prev.map(prop => {
        if (prop.skuPropertyId !== propertyId) return prop;
        
        return {
          ...prop,
          values: prop.values.map(val => 
            val.propertyValueId === valueId 
              ? { ...val, skip: !val.skip } 
              : val
          )
        };
      })
    );
  };

  const handleImport = async () => {
    const hasImportableVariation = product.variations.some(variation => {
      if (!variation.typeValues || variation.typeValues.length === 0) return false;

      return variation.typeValues.every((property) => {
        const mapping = propertyMappings.find(prop => prop.skuPropertyId === property.type.id);
        const value = mapping?.values.find(val => val.propertyValueId === property.id);
        return !!value && !value.skip;
      });
    });

    if (!hasImportableVariation) {
      toast.error('Please select at least one variation to import');
      return;
    }

    await SupplierCard_importVariations(adId, product, propertyMappings);
  };

  const modalActions: ModalAction[] = [
    {
      label: 'Cancel',
      onClick: onClose,
      preset: 'white'
    },
    {
      label: 'Import',
      onClick: handleImport,
      preset: 'primary'
    }
  ];

  return (
    <Modal
      title="Import Variations"
      onClose={onClose}
      actions={modalActions}
      width="large"
    >
      <div className="space-y-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="text-blue-800 font-medium mb-2">How this works</h3>
          <p className="text-blue-700 text-sm">
            1. For each supplier property (like "Color" or "Size"), edit the translations of the variation type to create.
            <br/>
            2. For each possible value (like "Red" or "XL"), edit the translations of the variation type value to create.
            <br/>
            3. Skip a value to leave out every variation that uses it.
            <br/>
            4. Ignore a type to still import its variations, without creating that type or its values.
            <br/>
            5. When you click "Import", we'll create the variation types, values, and variations based on your mappings.
          </p>
        </div>

        {propertyMappings.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <p className="text-gray-600">
              No variation properties found for this product.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {propertyMappings.map(property => (
              <div key={property.skuPropertyId} className="border rounded-lg p-4">
                <div className="flex items-start mb-4 gap-4">
                  <div className="flex items-center space-x-2 pt-2">
                    <input
                      type="checkbox"
                      id={`ignore-property-${property.skuPropertyId}`}
                      checked={property.ignore}
                      onChange={() => handlePropertyIgnoreToggle(property.skuPropertyId)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`ignore-property-${property.skuPropertyId}`} className="text-sm text-gray-700">
                      Ignore
                    </label>
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <TranslatedValue
                      translations={property.skuPropertyName}
                      renderAs="text"
                      disabled
                      onEditTranslation={() => {}}
                    />
                    <TranslatedValue
                      translations={property.typeName}
                      renderAs="input"
                      placeholder="Enter variation type name"
                      disabled={property.ignore}
                      onEditTranslation={(updates) => handlePropertyTypeNameChange(property.skuPropertyId, updates)}
                    />
                  </div>
                </div>

                <div className="ml-8 border-l-2 border-blue-200 pl-4 space-y-3">
                  {property.values.map(value => (
                    <div key={value.propertyValueId} className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`skip-value-${value.propertyValueId}`}
                          checked={value.skip}
                          onChange={() => handleValueSkipToggle(property.skuPropertyId, value.propertyValueId)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`skip-value-${value.propertyValueId}`} className="text-sm text-gray-700">
                          Skip
                        </label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {value.propertyValueImage && (
                          <img 
                            src={value.propertyValueImage} 
                            alt={getTranslatedValue(value.propertyValueName)} 
                            className="w-40 h-40 object-cover rounded-sm border"
                            referrerPolicy="no-referrer"
                          />
                        )}
                        <div className="min-w-0 w-40">
                          <TranslatedValue
                            translations={value.propertyValueName}
                            renderAs="text"
                            disabled
                            onEditTranslation={() => {}}
                          />
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <TranslatedValue
                          translations={value.typeValueName}
                          renderAs="input"
                          placeholder="Enter variation type value name"
                          disabled={property.ignore || value.skip}
                          onEditTranslation={(updates) => handleValueTypeNameChange(property.skuPropertyId, value.propertyValueId, updates)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
